// components/InfoPanel.tsx
import { cn, Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@racoon/ui";
import { InfoType, useInfoPanel } from "../model/useInfoPanel";
import { RouteInfoCard } from "../components/route-info.card";
import { TruckInfoCard } from "../components/truck-info.card";
import { JobInfoCard } from "../components/job-info.card";
import { PickupLocationInfoCard } from "../components/pickup-info.card";
import { DepotInfoCard } from "../components/depot-info.card";
import { useMediaQuery } from "~/shared/hooks/useMediaQuery";
import { IcCancel, IcChevron } from "@racoon/icons";
import { Namespace, useT } from "@racoon/i18n";
import { useState } from "react";

function renderByType(type: InfoType, id: string, setHeader: (element: React.ReactNode) => void) {
  switch (type) {
    case "route":
      return <RouteInfoCard id={id} setHeader={setHeader} />;
    case "truck":
      return <TruckInfoCard id={id} setHeader={setHeader} />;
    case "job":
      return <JobInfoCard id={id} setHeader={setHeader} />;
    case "pickup":
      return <PickupLocationInfoCard id={id} setHeader={setHeader} />;
    case "depot":
      return <DepotInfoCard id={id} setHeader={setHeader} />;
    default:
      return null;
  }
}

export function InfoPanel() {
  const t = useT();
  const { infoType, infoId, isOpen, close, canGoBack, back } = useInfoPanel();
  const backButton = canGoBack && (
    <button
      type="button"
      onClick={back}
      title={t(Namespace.Common, "back")}
      aria-label={t(Namespace.Common, "back")}
      className="-ml-1 mr-1 shrink-0 rounded p-0.5 hover:bg-brand-blue-light"
    >
      <IcChevron className="h-5 w-5 rotate-90" />
    </button>
  );
  const [headerElement, setHeaderElement] = useState<React.ReactNode>(null);
  const isDesktop = useMediaQuery("(min-width: 780px)");

  const body = infoType && infoId ? renderByType(infoType, String(infoId), setHeaderElement) : null;

  if (isDesktop) {
    return (
      <aside
        aria-hidden={!isOpen}
        className={[
          "transition-[width,opacity]",
          "duration-200 ease-out rounded-xl shrink-0",
          "border border-slate-200 bg-white",
          isOpen ? "w-[280px] opacity-100 ml-4" : "w-0 opacity-0 pointer-events-none",
        ].join(" ")}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between p-4 border-b">
            {backButton}
            <div className="flex-1">{headerElement}</div>
            <IcCancel className="h-6 w-6 cursor-pointer" onClick={close} />
          </div>
          <div className="flex-1 overflow-auto">{body}</div>
        </div>
      </aside>
    );
  }

  // MOBILE/TABLET: Drawer
  return (
    <Drawer open={isOpen} onOpenChange={(o) => !o && close()}>
      <DrawerContent className={cn("w-[100vw]  p-0 ")}>
        <DrawerHeader className="px-4 py-3 border-b">
          <div className="flex items-center justify-between">
            {backButton}
            <DrawerTitle className="flex-1">{headerElement || "Информация"}</DrawerTitle>
            <IcCancel className="h-6 w-6 cursor-pointer" onClick={close} />
          </div>
        </DrawerHeader>
        <div className="max-h-[calc(100vh-60px)] overflow-auto">{body}</div>
      </DrawerContent>
    </Drawer>
  );
}
